import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ZoomIn, ZoomOut, Maximize2, Minimize2, RotateCcw, X, Bus, ArrowUp, ArrowDown, Bath, DoorOpen, Tv } from "lucide-react";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

// Seat status colors
const SEAT_COLORS = {
  available: '#22c55e',    // Green
  selected: '#3b82f6',     // Blue
  booked: '#9ca3af',       // Gray
  held: '#fbbf24',         // Amber
};

// Helper function to get contrasting text color (white or black) based on background
const getContrastingTextColor = (hexColor) => {
  if (!hexColor) return '#ffffff';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  // Calculate luminance - using higher threshold (0.6) so green gets white text
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1e293b' : '#ffffff';
};

export default function PublicWidget() {
  const { layoutId } = useParams();
  const [searchParams] = useSearchParams();
  const [layout, setLayout] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(0.9);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [holdTimeLeft, setHoldTimeLeft] = useState(0);
  const [holdTimerActive, setHoldTimerActive] = useState(false);
  const [showTimerPopup, setShowTimerPopup] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [warningShown, setWarningShown] = useState(false);
  const [isWhiteLabel, setIsWhiteLabel] = useState(false);
  const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);
  const [subscriptionBlockReason, setSubscriptionBlockReason] = useState('');
  const [ticketPricing, setTicketPricing] = useState(null); // { pricing: [{category, price}], currency, seat_overrides }

  const embedMode = searchParams.get('embed') === 'true';
  const hideHeader = searchParams.get('hideHeader') === 'true';
  const holdMinutes = parseInt(searchParams.get('holdMinutes')) || 10;

  // Helper: get ticket price for a seat based on its zone/category
  const getSeatPrice = useCallback((seatRef) => {
    if (!ticketPricing) return null;
    // Check per-seat override first
    const seatOverride = (ticketPricing.seat_overrides || []).find(s => s.seat_ref === seatRef);
    if (seatOverride) return seatOverride.price;
    // Match by category/zone from seat data
    return null; // Will be resolved via zone lookup
  }, [ticketPricing]);

  const getCategoryPrice = useCallback((categoryName) => {
    if (!ticketPricing) return null;
    const entry = (ticketPricing.pricing || []).find(p => 
      p.category.toLowerCase() === categoryName.toLowerCase()
    );
    return entry ? entry.price : null;
  }, [ticketPricing]);

  const formatPrice = useCallback((price) => {
    if (price === null || price === undefined) return '';
    const currency = ticketPricing?.currency || 'GBP';
    const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency + ' ';
    return `${symbol}${price.toFixed(2)}`;
  }, [ticketPricing]);

  // Get price for a specific seat by its ref, checking overrides then category
  const getPriceForSeat = useCallback((seatRef) => {
    if (!ticketPricing) return null;
    // Check per-seat override
    const override = (ticketPricing.seat_overrides || []).find(s => s.seat_ref === seatRef);
    if (override) return override.price;
    // Look up category from seat_categories map (returned by public pricing endpoint)
    const seatCategories = ticketPricing.seat_categories || {};
    let category = seatCategories[seatRef];
    // Fallback: try deriving from seat_ref with zone prefix (e.g., "VIP - A1")
    if (!category) {
      const parts = seatRef.split(' - ');
      category = parts.length > 1 ? parts[0] : 'General';
    }
    const entry = (ticketPricing.pricing || []).find(p => 
      p.category.toLowerCase() === category.toLowerCase()
    );
    return entry ? entry.price : null;
  }, [ticketPricing]);

  // Calculate total for selected seats including fees
  const selectedSeatsPricing = React.useMemo(() => {
    if (!ticketPricing || selectedSeats.length === 0) return null;
    let ticketSubtotal = 0;
    let totalServiceFees = 0;
    let hasPrice = false;
    const serviceFeePercent = ticketPricing.service_fee?.percent || 0;
    const serviceFeeFixed = ticketPricing.service_fee?.fixed || 0;
    const seatFee = ticketPricing.seat_fee || 0;
    
    for (const s of selectedSeats) {
      const ref = typeof s === 'object' ? (s.seat_ref || s.label || s.id) : s;
      const price = getPriceForSeat(ref);
      if (price !== null) {
        ticketSubtotal += price;
        const sf = Math.round(((price * serviceFeePercent / 100) + serviceFeeFixed) * 100) / 100;
        totalServiceFees += sf;
        hasPrice = true;
      }
    }
    if (!hasPrice) return null;
    const totalSeatFees = Math.round(seatFee * selectedSeats.length * 100) / 100;
    const totalFees = Math.round((totalServiceFees + totalSeatFees) * 100) / 100;
    const grandTotal = Math.round((ticketSubtotal + totalFees) * 100) / 100;
    return { ticketSubtotal, totalServiceFees, totalSeatFees, totalFees, grandTotal };
  }, [selectedSeats, ticketPricing, getPriceForSeat]);
  const selectedSeatsTotal = selectedSeatsPricing?.grandTotal ?? null;
  // Popup configuration options (can be disabled via URL params)
  const showTimerNotification = searchParams.get('showTimerNotification') !== 'false'; // default: true
  const showWarningNotification = searchParams.get('showWarningNotification') !== 'false'; // default: true
  const warningThreshold = parseInt(searchParams.get('warningThreshold')) || 25; // default: 25% time remaining

  // ESC to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Hold timer
  useEffect(() => {
    if (selectedSeats.length > 0 && !holdTimerActive) {
      setHoldTimeLeft(holdMinutes * 60);
      setHoldTimerActive(true);
      setWarningShown(false);
      // Show initial timer popup
      if (showTimerNotification) {
        setShowTimerPopup(true);
        // Auto-hide after 6 seconds
        setTimeout(() => setShowTimerPopup(false), 6000);
      }
    }
    if (selectedSeats.length === 0) {
      setHoldTimerActive(false);
      setHoldTimeLeft(0);
      setShowTimerPopup(false);
      setShowWarningPopup(false);
      setWarningShown(false);
    }
  }, [selectedSeats.length, holdMinutes, holdTimerActive, showTimerNotification]);

  useEffect(() => {
    if (!holdTimerActive) return;
    const totalSeconds = holdMinutes * 60;
    const warningTime = Math.floor(totalSeconds * (warningThreshold / 100));
    
    const timer = setInterval(() => {
      setHoldTimeLeft(prev => {
        if (prev <= 1) {
          // Timer expired - release all seats
          setSelectedSeats([]);
          setHoldTimerActive(false);
          setShowTimerPopup(false);
          setShowWarningPopup(false);
          toast.error('Session expired! Your seat selection has been released. Please select seats again.');
          return 0;
        }
        
        // Check for warning threshold (25% time remaining by default)
        if (showWarningNotification && !warningShown && prev <= warningTime && prev > warningTime - 2) {
          setShowWarningPopup(true);
          setWarningShown(true);
          // Auto-hide after 5 seconds
          setTimeout(() => setShowWarningPopup(false), 5000);
        }
        
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [holdTimerActive, holdMinutes, warningThreshold, showWarningNotification, warningShown]);

  // Fetch layout
  useEffect(() => {
    const fetchLayout = async () => {
      try {
        // First try to fetch the layout directly
        const res = await axios.get(`${API}/admin/layouts/${layoutId}`);
        setLayout(res.data);
        
        // Check if layout is published - if not, we're in preview mode
        const isUnpublished = !res.data?.is_published;
        
        // Only check booking status for published layouts (not preview mode)
        if (!isUnpublished) {
          try {
            const bookingStatus = await axios.get(`${API}/public/layouts/${layoutId}/booking-status`);
            if (!bookingStatus.data?.can_accept_bookings) {
              setSubscriptionBlocked(true);
              setSubscriptionBlockReason(bookingStatus.data?.reason || 'Bookings are not available for this event.');
            }
          } catch (err) {
            // If endpoint fails, allow normal flow (backward compatibility)
            console.warn('Could not check booking status:', err);
          }
        }
        
        // Check white_label status from the layout's company
        if (res.data?.company_id) {
          try {
            const companyRes = await axios.get(`${API}/admin/company/${res.data.company_id}/white-label-status`);
            setIsWhiteLabel(companyRes.data?.white_label === true);
          } catch {
            setIsWhiteLabel(false);
          }
        } else {
          setIsWhiteLabel(false);
        }
        
        // Fetch ticket pricing
        try {
          const pricingRes = await axios.get(`${API}/public/layouts/${layoutId}/pricing`);
          if (pricingRes.data?.has_pricing) {
            setTicketPricing(pricingRes.data);
          }
        } catch {
          // No pricing set - that's fine
        }
      } catch (err) {
        toast.error('Failed to load seating layout');
      } finally {
        setLoading(false);
      }
    };
    if (layoutId) fetchLayout();
  }, [layoutId]);

  // Build zone lookup with bounds (moved up so it's available for helper functions)
  const zonesList = layout?.canvas_objects?.filter(obj => 
    obj.type === 'zone' || obj.type === 'ga_zone' || obj.type === 'vip_pod'
  ).map(obj => ({
    id: obj.id,
    label: obj.label || 'Zone',
    x: obj.x || 0,
    y: obj.y || 0,
    width: obj.width || 200,
    height: obj.height || 150
  })) || [];

  // Check if an object is visually inside any zone (by coordinates)
  const findContainingZone = (objX, objY, objWidth = 50, objHeight = 50) => {
    for (const zone of zonesList) {
      const objCenterX = objX + objWidth / 2;
      const objCenterY = objY + objHeight / 2;
      if (
        objCenterX >= zone.x && 
        objCenterX <= zone.x + zone.width &&
        objCenterY >= zone.y && 
        objCenterY <= zone.y + zone.height
      ) {
        return zone.label;
      }
    }
    return null;
  };

  // Get zone name - first check properties.zone, then check visual containment
  const getZoneForObject = (obj) => {
    if (!obj) return null;
    // First check if zone is explicitly set (could be zone ID or zone label)
    if (obj.properties?.zone) {
      // Try to find zone by ID first
      const zoneById = zonesList.find(z => z.id === obj.properties.zone);
      if (zoneById) return zoneById.label;
      // Then try by label (in case properties.zone contains the label string)
      const zoneByLabel = zonesList.find(z => z.label === obj.properties.zone);
      if (zoneByLabel) return zoneByLabel.label;
      // If zone property is set but zone not found, DON'T use it directly
      // Instead fall through to visual containment check (zone might have been renamed)
    }
    // Check if object is visually inside a zone (also handles renamed zones)
    return findContainingZone(obj.x || 0, obj.y || 0, obj.width || 50, obj.height || 50);
  };

  // Get all seats for table (with visual zone detection)
  const getTableSeats = (tableLabel) => {
    const tableObj = layout?.canvas_objects?.find(obj => 
      (obj.type === 'round_table' || obj.type === 'rect_table') && obj.label === tableLabel
    );
    if (!tableObj?.seats) return [];
    
    const zoneName = getZoneForObject(tableObj);
    const zonePrefix = zoneName ? `${zoneName} - ` : '';
    
    return tableObj.seats.map((seat, idx) => `${zonePrefix}${tableLabel}-${seat.seat_number || idx + 1}`);
  };

  // Get all seats for sofa (with visual zone detection)
  const getSofaSeats = (sofaLabel) => {
    const sofaObj = layout?.canvas_objects?.find(obj => obj.type === 'sofa' && obj.label === sofaLabel);
    if (!sofaObj?.seats) return [];
    
    const zoneName = getZoneForObject(sofaObj);
    const zonePrefix = zoneName ? `${zoneName} - ` : '';
    
    return sofaObj.seats.map((seat, idx) => `${zonePrefix}${sofaLabel}-${seat.seat_number || idx + 1}`);
  };

  // Check if book whole enabled
  const isBookWholeEnabled = (objType, objLabel) => {
    const obj = layout?.canvas_objects?.find(o => o.type === objType && o.label === objLabel);
    return obj?.properties?.book_whole_table || obj?.properties?.booking_mode === 'whole';
  };

  // Toggle seat
  const toggleSeat = (seatId, objType, objLabel) => {
    if ((objType === 'round_table' || objType === 'rect_table') && isBookWholeEnabled(objType, objLabel)) {
      const allSeats = getTableSeats(objLabel);
      const allSelected = allSeats.every(s => selectedSeats.includes(s));
      setSelectedSeats(prev => allSelected ? prev.filter(s => !allSeats.includes(s)) : [...new Set([...prev, ...allSeats])]);
      return;
    }
    
    if (objType === 'sofa' && isBookWholeEnabled('sofa', objLabel)) {
      const allSeats = getSofaSeats(objLabel);
      const allSelected = allSeats.every(s => selectedSeats.includes(s));
      setSelectedSeats(prev => allSelected ? prev.filter(s => !allSeats.includes(s)) : [...new Set([...prev, ...allSeats])]);
      return;
    }
    
    // For bus layout, use seatRef (objLabel) as the identifier if seatId is not available
    const identifier = seatId || objLabel;
    
    // Get bus layout for booking rules enforcement
    const busLayout = layout?.canvas_objects?.find(obj => obj.type === 'bus_layout');
    const bookingRules = busLayout?.properties?.booking_rules || [];
    
    // Parse seat reference to get row number and side
    const seatRef = objLabel;
    const rowMatch = seatRef?.match(/^(\d+)([A-Z]+)$/);
    const rowNum = rowMatch ? parseInt(rowMatch[1]) : null;
    const seatLetter = rowMatch ? rowMatch[2] : null;
    
    // Determine which side this seat is on
    const leftSeats = busLayout?.properties?.left_seats || 2;
    const rightSeats = busLayout?.properties?.right_seats || 2;
    const totalSeats = leftSeats + rightSeats;
    
    // Generate column letters if not provided - must cover all seats
    const defaultLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const columnLetters = busLayout?.properties?.column_letters || defaultLetters.slice(0, totalSeats);
    
    const seatIndex = columnLetters.indexOf(seatLetter);
    const seatSide = seatIndex < leftSeats ? 'left' : 'right';
    
    // Find applicable booking rule for this seat
    const applicableRule = bookingRules.find(rule => {
      // Check if row matches
      const rowMatches = rule.rows === 'all' || 
        (Array.isArray(rule.rows) && rule.rows.includes(rowNum));
      
      // Check if side matches
      const sideMatches = rule.side === 'both' || rule.side === seatSide;
      
      return rowMatches && sideMatches;
    });
    
    // Handle group booking rules
    if (applicableRule && (applicableRule.policy === 'group_only' || applicableRule.policy === 'either')) {
      const groupType = applicableRule.groupType || 'adjacent_pair';
      let groupSeats = [];
      
      if (groupType === 'adjacent_pair') {
        // Get adjacent pair on the same side
        if (seatSide === 'left') {
          // Left side seats (e.g., A, B)
          const leftLetters = columnLetters.slice(0, leftSeats);
          groupSeats = leftLetters.split('').map(letter => `${rowNum}${letter}`);
        } else {
          // Right side seats (e.g., C, D)
          const rightLetters = columnLetters.slice(leftSeats);
          groupSeats = rightLetters.split('').map(letter => `${rowNum}${letter}`);
        }
      } else if (groupType === 'full_row') {
        // All seats in the row
        groupSeats = columnLetters.split('').map(letter => `${rowNum}${letter}`);
      } else if (groupType === 'side_block') {
        // All seats on one side across all rows
        const startRowNum = busLayout?.properties?.start_row_number || 1;
        const rowCount = busLayout?.properties?.row_count || (busLayout?.seats ? Math.max(1, Math.max(...busLayout.seats.map(s => (s.row ?? 0))) + 1) : 9);
        for (let r = startRowNum; r < startRowNum + rowCount; r++) {
          if (seatSide === 'left') {
            const leftLetters = columnLetters.slice(0, leftSeats);
            leftLetters.split('').forEach(letter => groupSeats.push(`${r}${letter}`));
          } else {
            const rightLetters = columnLetters.slice(leftSeats);
            rightLetters.split('').forEach(letter => groupSeats.push(`${r}${letter}`));
          }
        }
      }
      
      // Check if this is a group_only policy
      if (applicableRule.policy === 'group_only') {
        // Show toast message about group booking
        toast.info(`Group booking required! Selecting ${groupSeats.length} seats together (${groupSeats.join(', ')})`, {
          duration: 3000
        });
        
        // Handle group selection with setSelectedSeats
        setSelectedSeats(prev => {
          // Check if any group seat is already selected
          const anyGroupSeatSelected = groupSeats.some(gs => 
            prev.some(s => typeof s === 'object' ? s.seat_ref === gs : s === gs)
          );
          
          if (anyGroupSeatSelected) {
            // Deselect all group seats
            return prev.filter(s => {
              const ref = typeof s === 'object' ? s.seat_ref : s;
              return !groupSeats.includes(ref);
            });
          } else {
            // Select all group seats together
            const newSeats = groupSeats.map(gs => ({
              id: null,
              seat_ref: gs,
              type: 'bus_layout'
            }));
            return [...prev, ...newSeats];
          }
        });
        return; // Exit after group selection
      }
    }
    
    // Check for "Prevent Middle Seat" rule
    const preventMiddleSeat = busLayout?.properties?.prevent_middle_seat;
    if (preventMiddleSeat && objType === 'bus_layout') {
      // Get seat count per side - check for custom row configuration first
      const customRowsEnabled = busLayout?.properties?.custom_rows_enabled;
      const customRows = busLayout?.properties?.custom_rows || {};
      const rowConfig = customRows[rowNum] || {};
      
      // Use custom row config if enabled, otherwise use default values
      const defaultLeftSeats = busLayout?.properties?.left_seats || 2;
      const defaultRightSeats = busLayout?.properties?.right_seats || 2;
      
      const actualLeftSeats = customRowsEnabled && rowConfig.left_seats !== undefined 
        ? rowConfig.left_seats 
        : defaultLeftSeats;
      const actualRightSeats = customRowsEnabled && rowConfig.right_seats !== undefined 
        ? rowConfig.right_seats 
        : defaultRightSeats;
      
      const sideSeatsCount = seatSide === 'left' ? actualLeftSeats : actualRightSeats;
      
      // Only applies to rows with 3+ seats per side
      if (sideSeatsCount >= 3) {
        // Get the letters for this side - TREAT LEFT AND RIGHT COMPLETELY SEPARATELY
        // Left side: first N letters of columnLetters
        // Right side: next M letters starting after left seats (use ACTUAL left count, not default)
        const leftSideLetters = columnLetters.slice(0, actualLeftSeats);
        const rightSideLetters = columnLetters.slice(defaultLeftSeats, defaultLeftSeats + actualRightSeats);
        
        const sideLetters = seatSide === 'left' ? leftSideLetters : rightSideLetters;
        
        // Find position of this seat letter WITHIN its own side (0-indexed)
        const seatPositionInSide = sideLetters.indexOf(seatLetter);
        
        // If seat letter not found in this side's letters, skip the check
        if (seatPositionInSide === -1) {
          // Seat letter not in this side, skip middle seat check
        } else {
          // Middle seat = not first (pos 0) and not last (pos sideSeatsCount-1) position
          const isMiddleSeat = seatPositionInSide > 0 && seatPositionInSide < sideSeatsCount - 1;
          
          if (isMiddleSeat) {
            // Build list of all seat references on this row/side
            const rowSideSeats = sideLetters.split('').map(letter => `${rowNum}${letter}`);
            const firstSeatOnSide = rowSideSeats[0];
            const lastSeatOnSide = rowSideSeats[sideSeatsCount - 1];
            
            // Get currently selected seats on this SPECIFIC row AND side only
            const selectedOnThisSide = selectedSeats.filter(s => {
              const ref = typeof s === 'object' ? s.seat_ref : s;
              return rowSideSeats.includes(ref);
            });
            
            const selectedRefs = selectedOnThisSide.map(s => typeof s === 'object' ? s.seat_ref : s);
            
            // If no seats selected on this side yet and trying to select middle, block it
            if (selectedRefs.length === 0) {
              toast.error(`Cannot select middle seat first! Please select an edge seat (${firstSeatOnSide} or ${lastSeatOnSide}) first.`, {
                duration: 4000
              });
              return;
            }
            
            // Check if the middle seat is adjacent to any ALREADY SELECTED seat on this side
            // Adjacent means position difference of 1 within the side's letter sequence
            const adjacentPositions = [seatPositionInSide - 1, seatPositionInSide + 1];
            const hasAdjacentSelected = selectedRefs.some(ref => {
              const letter = ref.replace(/^\d+/, ''); // Extract letter from "1D" -> "D"
              const pos = sideLetters.indexOf(letter);
              return adjacentPositions.includes(pos);
            });
            
            if (!hasAdjacentSelected) {
              toast.error(`Middle seat must be adjacent to a selected seat. Select ${firstSeatOnSide} or ${lastSeatOnSide} first.`, {
                duration: 4000
              });
              return;
            }
          }
        }
      }
    }
    
    // Check if this seat is already selected (for deselection logic)
    const isCurrentlySelected = selectedSeats.some(s => 
      (typeof s === 'object' ? (s.id === identifier || s.seat_ref === identifier) : s === identifier)
    );
    
    // PREVENT MIDDLE SEAT ORPHAN RULES (only when prevent_middle_seat is enabled)
    if (preventMiddleSeat && objType === 'bus_layout') {
      const defaultLeftSeats = busLayout?.properties?.left_seats || 2;
      const defaultRightSeats = busLayout?.properties?.right_seats || 2;
      const sideSeatsCount = seatSide === 'left' ? defaultLeftSeats : defaultRightSeats;
      
      // Only applies to rows with 3+ seats per side
      if (sideSeatsCount >= 3) {
        const leftSideLetters = columnLetters.slice(0, defaultLeftSeats);
        const rightSideLetters = columnLetters.slice(defaultLeftSeats, defaultLeftSeats + defaultRightSeats);
        const sideLetters = seatSide === 'left' ? leftSideLetters : rightSideLetters;
        const rowSideSeats = sideLetters.split('').map(letter => `${rowNum}${letter}`);
        
        // Get currently selected seats on this row/side
        const selectedOnThisSide = selectedSeats.filter(s => {
          const ref = typeof s === 'object' ? s.seat_ref : s;
          return rowSideSeats.includes(ref);
        });
        const selectedRefs = selectedOnThisSide.map(s => typeof s === 'object' ? s.seat_ref : s);
        
        // RULE 1: Auto-deselect orphaned middle seats when edge is deselected
        if (isCurrentlySelected) {
          const seatPositionInSide = sideLetters.indexOf(seatLetter);
          const isEdgeSeat = seatPositionInSide === 0 || seatPositionInSide === sideSeatsCount - 1;
          
          if (isEdgeSeat) {
            // Find middle seats that would be orphaned by removing this edge
            const orphanedMiddleSeats = [];
            
            for (let i = 1; i < sideSeatsCount - 1; i++) {
              const middleSeatRef = `${rowNum}${sideLetters[i]}`;
              if (selectedRefs.includes(middleSeatRef)) {
                // This middle seat is selected - check if removing this edge would orphan it
                const middlePos = i;
                const adjacentPositions = [middlePos - 1, middlePos + 1];
                const remainingSeatsAfterRemoval = selectedRefs.filter(ref => ref !== objLabel);
                
                const hasAdjacentAfterRemoval = remainingSeatsAfterRemoval.some(ref => {
                  const letter = ref.replace(/^\d+/, '');
                  const pos = sideLetters.indexOf(letter);
                  return adjacentPositions.includes(pos);
                });
                
                if (!hasAdjacentAfterRemoval) {
                  orphanedMiddleSeats.push(middleSeatRef);
                }
              }
            }
            
            // If there are orphaned middle seats, auto-deselect them along with the edge
            if (orphanedMiddleSeats.length > 0) {
              const seatsToRemove = [objLabel, ...orphanedMiddleSeats];
              toast.info(`Deselected ${objLabel} and middle seat(s) ${orphanedMiddleSeats.join(', ')} to avoid orphaned seats.`, {
                duration: 3000
              });
              
              setSelectedSeats(prev => prev.filter(s => {
                const ref = typeof s === 'object' ? s.seat_ref : s;
                return !seatsToRemove.includes(ref);
              }));
              return;
            }
          }
        }
        
        // RULE 2: Prevent deselecting middle seat if it would leave both edges selected (orphan gap)
        if (isCurrentlySelected) {
          const seatPositionInSide = sideLetters.indexOf(seatLetter);
          const isMiddleSeat = seatPositionInSide > 0 && seatPositionInSide < sideSeatsCount - 1;
          
          if (isMiddleSeat) {
            // Check if both edges are selected
            const firstEdgeRef = `${rowNum}${sideLetters[0]}`;
            const lastEdgeRef = `${rowNum}${sideLetters[sideSeatsCount - 1]}`;
            const bothEdgesSelected = selectedRefs.includes(firstEdgeRef) && selectedRefs.includes(lastEdgeRef);
            
            if (bothEdgesSelected) {
              // Deselecting middle would leave orphan gap - auto-deselect all seats on this side
              toast.info(`Deselected all seats on row ${rowNum} (${seatSide} side) to avoid orphan middle seat gap.`, {
                duration: 3000
              });
              
              setSelectedSeats(prev => prev.filter(s => {
                const ref = typeof s === 'object' ? s.seat_ref : s;
                return !rowSideSeats.includes(ref);
              }));
              return;
            }
          }
        }
        
        // RULE 3: Prevent selecting both edge seats if middle would be left empty (orphaned)
        if (!isCurrentlySelected) {
          const seatPositionInSide = sideLetters.indexOf(seatLetter);
          const isEdgeSeat = seatPositionInSide === 0 || seatPositionInSide === sideSeatsCount - 1;
          
          if (isEdgeSeat && selectedRefs.length > 0) {
            // Check if the other edge is already selected on this row/side
            const otherEdgePosition = seatPositionInSide === 0 ? sideSeatsCount - 1 : 0;
            const otherEdgeLetter = sideLetters[otherEdgePosition];
            const otherEdgeRef = `${rowNum}${otherEdgeLetter}`;
            
            if (selectedRefs.includes(otherEdgeRef)) {
              // Other edge is selected - check if all middle seats are also selected
              const middleSeatsRefs = [];
              for (let i = 1; i < sideSeatsCount - 1; i++) {
                middleSeatsRefs.push(`${rowNum}${sideLetters[i]}`);
              }
              
              const allMiddleSelected = middleSeatsRefs.every(ref => selectedRefs.includes(ref));
              
              if (!allMiddleSelected) {
                // Would leave middle seats orphaned
                const unselectedMiddle = middleSeatsRefs.filter(ref => !selectedRefs.includes(ref));
                toast.error(`Cannot select both edge seats! This would leave middle seat(s) ${unselectedMiddle.join(', ')} unselectable. Select the middle seat(s) first.`, {
                  duration: 4000
                });
                return;
              }
            }
          }
        }
      }
    }
    
    // Normal single seat selection (no rule or 'individual'/'either' policy)
    setSelectedSeats(prev => {
      const isSelected = prev.some(s => 
        (typeof s === 'object' ? (s.id === identifier || s.seat_ref === identifier) : s === identifier)
      );
      
      if (isSelected) {
        return prev.filter(s => 
          typeof s === 'object' 
            ? (s.id !== identifier && s.seat_ref !== identifier) 
            : s !== identifier
        );
      } else {
        // For bus layouts, store the seat reference for booking
        if (objType === 'bus_layout') {
          return [...prev, { id: seatId, seat_ref: objLabel, type: 'bus_layout' }];
        }
        return [...prev, identifier];
      }
    });
  };

  const clearAllSeats = () => {
    setSelectedSeats([]);
    setHoldTimerActive(false);
    setHoldTimeLeft(0);
  };

  const zoomIn = () => setZoom(z => Math.min(z + 0.15, 2));
  const zoomOut = () => setZoom(z => Math.max(z - 0.15, 0.3));
  const resetZoom = () => setZoom(0.9);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const getRowLabel = (num) => {
    let label = '';
    num++;
    while (num > 0) {
      num--;
      label = String.fromCharCode(65 + (num % 26)) + label;
      num = Math.floor(num / 26);
    }
    return label;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-700 text-lg">Loading seating chart...</div>
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-700 text-lg">Layout not found</div>
      </div>
    );
  }

  // Check if this is a preview of unpublished layout
  const isPreviewMode = !layout.is_published;

  // Show subscription blocked message ONLY for published layouts (not preview mode)
  // This allows unpublished layouts to be previewed even without payment method
  if (subscriptionBlocked && !isPreviewMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-xl shadow-lg">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Booking Unavailable</h2>
          <p className="text-gray-600 mb-4">{subscriptionBlockReason || 'Seat booking is currently unavailable for this event.'}</p>
          <p className="text-sm text-gray-500">Please contact the event organizer for assistance.</p>
        </div>
      </div>
    );
  }

  const displayMode = layout.layout_display_mode || 'auto';

  // Canvas bounds for manual mode
  const getCanvasBounds = () => {
    if (!layout.canvas_objects?.length) return { minX: 0, minY: 0, maxX: 800, maxY: 600 };
    
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    layout.canvas_objects.forEach(obj => {
      const objRight = (obj.x || 0) + (obj.width || 100) + 50;
      const objBottom = (obj.y || 0) + (obj.height || 100) + 50;
      minX = Math.min(minX, (obj.x || 0) - 50);
      minY = Math.min(minY, (obj.y || 0) - 30);
      maxX = Math.max(maxX, objRight);
      maxY = Math.max(maxY, objBottom);
    });
    
    return { minX, minY, maxX, maxY };
  };

  // Process seats for AUTO mode
  const processSeatsAuto = () => {
    const allSeats = [];
    
    layout.canvas_objects?.forEach(obj => {
      // Use the new zone detection that checks visual containment
      const zoneName = getZoneForObject(obj);
      const zonePrefix = zoneName ? `${zoneName} - ` : '';
      
      // Single Row
      if (obj.type === 'single_row' && obj.seats) {
        const rowLabel = obj.properties?.row_label || 'A';
        const curve = obj.properties?.curve_amount || 0;
        obj.seats.forEach((seat, idx) => {
          const seatLabel = `${rowLabel}${seat.seat_number}`;
          allSeats.push({
            id: `${zonePrefix}${seatLabel}`,
            displayLabel: seatLabel,
            row: zoneName ? `📍 ${zoneName} - Row ${rowLabel}` : rowLabel,
            seatNum: seat.seat_number,
            curve,
            type: 'row',
            objType: 'single_row',
            objLabel: rowLabel
          });
        });
      }
      
      // Multiple Rows
      if (obj.type === 'multiple_rows' && obj.seats) {
        const startChar = obj.properties?.start_label || 'A';
        const startNum = startChar.charCodeAt(0) - 65;
        const curve = obj.properties?.curve_amount || 0;
        
        obj.seats.forEach(seat => {
          const rowLabel = getRowLabel(startNum + (seat.row || 0));
          const seatLabel = `${rowLabel}${seat.seat_number}`;
          allSeats.push({
            id: `${zonePrefix}${seatLabel}`,
            displayLabel: seatLabel,
            row: zoneName ? `📍 ${zoneName} - Row ${rowLabel}` : rowLabel,
            seatNum: seat.seat_number,
            curve,
            type: 'row',
            objType: 'multiple_rows',
            objLabel: rowLabel
          });
        });
      }
      
      // Sofa
      if (obj.type === 'sofa' && obj.seats) {
        const sofaLabel = obj.label || 'Sofa';
        const displayName = sofaLabel.match(/^S\d+$/) ? `Sofa ${sofaLabel.slice(1)}` : sofaLabel;
        const count = obj.seats.length;
        const bookWhole = obj.properties?.booking_mode === 'whole' || obj.properties?.book_whole_table;
        obj.seats.forEach((seat, idx) => {
          allSeats.push({
            id: `${zonePrefix}${sofaLabel}-${seat.seat_number || idx + 1}`,
            displayLabel: `${seat.seat_number || idx + 1}`,
            row: `🛋️ ${zonePrefix}${displayName} (${count}-seater)${bookWhole ? ' - Book Whole' : ''}`,
            seatNum: seat.seat_number || idx + 1,
            type: 'sofa',
            objType: 'sofa',
            objLabel: sofaLabel,
            bookWhole
          });
        });
      }
      
      // Round Table
      if (obj.type === 'round_table' && obj.seats) {
        const tableLabel = obj.label || 'Table';
        const displayName = tableLabel.match(/^T\d+$/) ? `Table ${tableLabel.slice(1)}` : tableLabel;
        const count = obj.seats.length;
        const bookWhole = obj.properties?.book_whole_table;
        obj.seats.forEach((seat, idx) => {
          allSeats.push({
            id: `${zonePrefix}${tableLabel}-${seat.seat_number || idx + 1}`,
            displayLabel: `${seat.seat_number || idx + 1}`,
            row: `🪑 ${zonePrefix}${displayName} (${count} seats)${bookWhole ? ' - Book Whole' : ''}`,
            seatNum: seat.seat_number || idx + 1,
            type: 'round_table',
            objType: 'round_table',
            objLabel: tableLabel,
            bookWhole
          });
        });
      }
      
      // Rect Table
      if (obj.type === 'rect_table' && obj.seats) {
        const tableLabel = obj.label || 'Table';
        const displayName = tableLabel.match(/^T\d+$/) ? `Table ${tableLabel.slice(1)}` : tableLabel;
        const count = obj.seats.length;
        const bookWhole = obj.properties?.book_whole_table;
        obj.seats.forEach((seat, idx) => {
          allSeats.push({
            id: `${zonePrefix}${tableLabel}-${seat.seat_number || idx + 1}`,
            displayLabel: `${seat.seat_number || idx + 1}`,
            row: `🪑 ${zonePrefix}${displayName} (${count} seats)${bookWhole ? ' - Book Whole' : ''}`,
            seatNum: seat.seat_number || idx + 1,
            type: 'rect_table',
            objType: 'rect_table',
            objLabel: tableLabel,
            bookWhole
          });
        });
      }
      
      // Zone with seats
      if ((obj.type === 'zone' || obj.type === 'ga_zone' || obj.type === 'vip_pod') && obj.seats) {
        const zoneLabel = obj.label || 'Zone';
        obj.seats.forEach((seat, idx) => {
          const seatLabel = seat.label || `${idx + 1}`;
          allSeats.push({
            id: `${zoneLabel} - ${seatLabel}`,
            displayLabel: seatLabel,
            row: `📍 ${zoneLabel}`,
            seatNum: idx + 1,
            type: 'zone_seat',
            objType: 'zone',
            objLabel: zoneLabel
          });
        });
      }
    });
    
    return allSeats;
  };

  // MANUAL MODE RENDER
  const renderManualMode = () => {
    const bounds = getCanvasBounds();
    const canvasWidth = bounds.maxX - bounds.minX;
    const canvasHeight = bounds.maxY - bounds.minY;
    
    return (
      <div 
        className="relative mx-auto"
        style={{ 
          width: canvasWidth,
          height: canvasHeight,
          transform: `scale(${zoom})`,
          transformOrigin: 'top center'
        }}
      >
        {/* Layer 1: Screen objects (new draggable screen type) */}
        {layout.canvas_objects?.filter(obj => obj.type === 'screen').map(obj => (
          <div 
            key={obj.id}
            className="absolute flex items-center justify-center shadow-lg"
            style={{
              left: obj.x - bounds.minX,
              top: obj.y - bounds.minY,
              width: obj.width || 400,
              height: obj.height || 60,
              backgroundColor: '#7c3aed',
              borderRadius: '8px',
              zIndex: 1
            }}
          >
            <span className="text-white font-bold text-base tracking-widest">{obj.label || 'SCREEN'}</span>
          </div>
        ))}
        
        {/* Layer 1b: Stage & DJ Booth */}
        {layout.canvas_objects?.filter(obj => obj.type === 'stage' || obj.type === 'dj_booth').map(obj => (
          <div 
            key={obj.id}
            className="absolute flex items-center justify-center shadow-lg"
            style={{
              left: obj.x - bounds.minX,
              top: obj.y - bounds.minY,
              width: obj.width || 200,
              height: obj.height || 80,
              backgroundColor: obj.type === 'stage' ? '#a855f7' : '#06b6d4',
              borderRadius: obj.type === 'stage' ? '0 0 24px 24px' : '12px',
              zIndex: 1
            }}
          >
            <span className="text-white font-medium text-sm tracking-wider">{obj.label || 'STAGE'}</span>
          </div>
        ))}
        
        {/* Layer 2: Zones */}
        {layout.canvas_objects?.filter(obj => obj.type === 'zone' || obj.type === 'ga_zone' || obj.type === 'vip_pod').map(obj => {
          const borderColor = obj.properties?.border_color || obj.color || '#3b82f6';
          const borderStyle = obj.properties?.border_style || 'dashed';
          const bgColor = obj.properties?.bg_color || obj.color || '#dbeafe';
          
          return (
            <div 
              key={obj.id}
              className="absolute rounded-lg"
              style={{
                left: obj.x - bounds.minX,
                top: obj.y - bounds.minY,
                width: obj.width || 200,
                height: obj.height || 150,
                backgroundColor: `${bgColor}30`,
                borderWidth: '2px',
                borderStyle: borderStyle,
                borderColor: borderColor,
                zIndex: 2
              }}
            >
              <div 
                className="absolute -top-3 left-2 px-2 text-xs font-bold rounded text-white"
                style={{ backgroundColor: borderColor }}
              >
                {obj.label}
              </div>
            </div>
          );
        })}
        
        {/* Layer 3: Tables (HIGHER z-index to be above rows) */}
        {layout.canvas_objects?.filter(obj => obj.type === 'round_table' || obj.type === 'rect_table').map(obj => {
          const tableLabel = obj.label || 'Table';
          const displayName = tableLabel.match(/^T\d+$/) ? `Table ${tableLabel.slice(1)}` : tableLabel;
          // Use new zone detection based on visual containment
          const zoneName = getZoneForObject(obj);
          const zonePrefix = zoneName ? `${zoneName} - ` : '';
          const bookWhole = obj.properties?.book_whole_table;
          const count = obj.seats?.length || 0;
          
          const allSeatIds = obj.seats?.map((s, i) => `${zonePrefix}${tableLabel}-${s.seat_number || i + 1}`) || [];
          const allSelected = allSeatIds.every(id => selectedSeats.includes(id));
          
          return (
            <div key={obj.id}>
              {/* Table surface with label INSIDE */}
              <div 
                className="absolute flex flex-col items-center justify-center shadow-md"
                style={{
                  left: obj.x - bounds.minX,
                  top: obj.y - bounds.minY,
                  width: obj.width || 100,
                  height: obj.height || 100,
                  backgroundColor: allSelected ? '#dbeafe' : '#e2e8f0',
                  borderRadius: obj.type === 'round_table' ? '50%' : '8px',
                  border: allSelected ? '3px solid #3b82f6' : '3px solid #94a3b8',
                  zIndex: 50
                }}
              >
                <span className="text-[10px] font-bold text-gray-700 text-center px-1 leading-tight">
                  {displayName}
                </span>
                <span className="text-[8px] text-gray-500 text-center px-1">
                  ({count}){bookWhole ? ' - Full table' : ''}
                </span>
              </div>
              {/* Table seats */}
              {obj.seats?.map((seat, idx) => {
                const seatId = `${zonePrefix}${tableLabel}-${seat.seat_number || idx + 1}`;
                const isSelected = selectedSeats.includes(seatId);
                const tableWidth = obj.width || 100;
                const tableHeight = obj.height || 100;
                
                let seatX, seatY;
                
                if (obj.type === 'rect_table') {
                  // Rectangular table - position seats along edges evenly
                  const seatCount = obj.seats.length;
                  
                  // Calculate how many seats per side based on table proportions
                  const perimeter = 2 * (tableWidth + tableHeight);
                  const topBottomRatio = tableWidth / perimeter;
                  const leftRightRatio = tableHeight / perimeter;
                  
                  // Distribute seats proportionally to each side
                  const topSeats = Math.max(1, Math.round(seatCount * topBottomRatio));
                  const rightSeats = Math.max(1, Math.round(seatCount * leftRightRatio));
                  const bottomSeats = Math.max(1, Math.round(seatCount * topBottomRatio));
                  const leftSeats = Math.max(0, seatCount - topSeats - rightSeats - bottomSeats);
                  
                  // Determine which side this seat is on
                  let side, posOnSide, seatsOnThisSide;
                  if (idx < topSeats) {
                    side = 0; // Top
                    posOnSide = idx;
                    seatsOnThisSide = topSeats;
                  } else if (idx < topSeats + rightSeats) {
                    side = 1; // Right
                    posOnSide = idx - topSeats;
                    seatsOnThisSide = rightSeats;
                  } else if (idx < topSeats + rightSeats + bottomSeats) {
                    side = 2; // Bottom
                    posOnSide = idx - topSeats - rightSeats;
                    seatsOnThisSide = bottomSeats;
                  } else {
                    side = 3; // Left
                    posOnSide = idx - topSeats - rightSeats - bottomSeats;
                    seatsOnThisSide = leftSeats || 1;
                  }
                  
                  const sideLength = (side % 2 === 0) ? tableWidth : tableHeight;
                  const spacing = sideLength / (seatsOnThisSide + 1);
                  
                  switch (side) {
                    case 0: // Top
                      seatX = spacing * (posOnSide + 1) - 12;
                      seatY = -18;
                      break;
                    case 1: // Right
                      seatX = tableWidth + 6;
                      seatY = spacing * (posOnSide + 1) - 12;
                      break;
                    case 2: // Bottom
                      seatX = tableWidth - spacing * (posOnSide + 1) - 12;
                      seatY = tableHeight + 6;
                      break;
                    case 3: // Left
                    default:
                      seatX = -18;
                      seatY = tableHeight - spacing * (posOnSide + 1) - 12;
                      break;
                  }
                } else {
                  // Round table - use circular positioning
                  const centerX = tableWidth / 2;
                  const centerY = tableHeight / 2;
                  const radius = Math.max(tableWidth, tableHeight) / 2 + 18;
                  const angle = (seat.angle || (360 / count) * idx) * (Math.PI / 180);
                  seatX = centerX + radius * Math.cos(angle - Math.PI / 2) - 12;
                  seatY = centerY + radius * Math.sin(angle - Math.PI / 2) - 12;
                }
                
                // Use custom seat color if set
                const customSeatColor = obj.properties?.seat_color;
                const availableColor = customSeatColor || SEAT_COLORS.available;
                const bgColor = isSelected ? SEAT_COLORS.selected : availableColor;
                const textColor = getContrastingTextColor(bgColor);
                
                return (
                  <button
                    key={idx}
                    onClick={() => toggleSeat(seatId, obj.type, tableLabel)}
                    className={`absolute flex items-center justify-center font-bold shadow-md transition-all ${
                      isSelected ? 'ring-2 ring-blue-700 scale-110' : 'hover:scale-105'
                    }`}
                    style={{
                      left: obj.x - bounds.minX + seatX,
                      top: obj.y - bounds.minY + seatY,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: bgColor,
                      color: textColor,
                      fontSize: '10px',
                      zIndex: 51
                    }}
                    title={seatId}
                  >
                    {seat.seat_number || idx + 1}
                  </button>
                );
              })}
            </div>
          );
        })}
        
        {/* Layer 4: Rows */}
        {layout.canvas_objects?.filter(obj => obj.type === 'single_row' || obj.type === 'multiple_rows').map(obj => {
          // Use new zone detection
          const zoneName = getZoneForObject(obj);
          const zonePrefix = zoneName ? `${zoneName} - ` : '';
          const startLabel = obj.properties?.row_label || obj.properties?.start_label || 'A';
          const startNum = startLabel.charCodeAt(0) - 65;
          
          // For multiple rows, group by row
          const rowGroups = {};
          obj.seats?.forEach(seat => {
            const rowLabel = obj.type === 'single_row' ? startLabel : getRowLabel(startNum + (seat.row || 0));
            if (!rowGroups[rowLabel]) rowGroups[rowLabel] = [];
            rowGroups[rowLabel].push(seat);
          });
          
          const maxX = Math.max(...(obj.seats?.map(s => s.x || 0) || [0]));
          
          return (
            <div key={obj.id} className="absolute" style={{ left: obj.x - bounds.minX, top: obj.y - bounds.minY, zIndex: 5 }}>
              {/* Render each row with its own label */}
              {Object.entries(rowGroups).map(([rowLabel, seats], rowIdx) => {
                const firstSeat = seats[0];
                const rowY = firstSeat?.y || rowIdx * 36;
                
                return (
                  <div key={rowLabel} className="relative">
                    {/* Left label for this specific row */}
                    <div 
                      className="absolute text-gray-600 text-sm font-bold"
                      style={{ left: -35, top: rowY + 4 }}
                    >
                      {rowLabel}
                    </div>
                    {/* Right label */}
                    <div 
                      className="absolute text-gray-600 text-sm font-bold"
                      style={{ left: maxX + 45, top: rowY + 4 }}
                    >
                      {rowLabel}
                    </div>
                    {/* Seats for this row */}
                    {seats.map((seat, idx) => {
                      const seatLabel = `${rowLabel}${seat.seat_number}`;
                      const seatId = `${zonePrefix}${seatLabel}`;
                      const isSelected = selectedSeats.includes(seatId);
                      // Use custom seat color from object properties if set
                      const customSeatColor = obj.properties?.seat_color;
                      const availableColor = customSeatColor || SEAT_COLORS.available;
                      const bgColor = isSelected ? SEAT_COLORS.selected : availableColor;
                      const textColor = getContrastingTextColor(bgColor);
                      
                      return (
                        <button
                          key={idx}
                          onClick={() => toggleSeat(seatId, obj.type, rowLabel)}
                          className={`absolute flex items-center justify-center font-bold shadow-md transition-all ${
                            isSelected ? 'ring-2 ring-blue-700 scale-110' : 'hover:scale-105'
                          }`}
                          style={{
                            left: seat.x || idx * 36,
                            top: seat.y || 0,
                            width: 32,
                            height: 26,
                            borderRadius: '6px 6px 3px 3px',
                            backgroundColor: bgColor,
                            color: textColor,
                            fontSize: '10px',
                            zIndex: 6
                          }}
                          title={seatId}
                        >
                          {seatLabel}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
        
        {/* Layer 5: Sofas */}
        {layout.canvas_objects?.filter(obj => obj.type === 'sofa').map(obj => {
          const sofaLabel = obj.label || 'Sofa';
          const displayName = sofaLabel.match(/^S\d+$/) ? `Sofa ${sofaLabel.slice(1)}` : sofaLabel;
          // Use new zone detection
          const zoneName = getZoneForObject(obj);
          const zonePrefix = zoneName ? `${zoneName} - ` : '';
          const bookWhole = obj.properties?.booking_mode === 'whole' || obj.properties?.book_whole_table;
          const count = obj.seats?.length || 0;
          
          const allSeatIds = obj.seats?.map((s, i) => `${zonePrefix}${sofaLabel}-${s.seat_number || i + 1}`) || [];
          const allSelected = allSeatIds.every(id => selectedSeats.includes(id));
          
          return (
            <div key={obj.id}>
              {/* Sofa label - HIGH z-index */}
              <div 
                className="absolute text-gray-600 text-xs font-medium whitespace-nowrap bg-white/90 px-1 rounded pointer-events-none"
                style={{
                  left: obj.x - bounds.minX + (obj.width || 150) / 2,
                  top: obj.y - bounds.minY - 22,
                  transform: 'translateX(-50%)',
                  zIndex: 100
                }}
              >
                🛋️ {zonePrefix}{displayName} ({count}-seater){bookWhole ? ' - Full sofa' : ''}
              </div>
              {/* Sofa frame */}
              <div 
                className="absolute rounded-xl shadow-md"
                style={{
                  left: obj.x - bounds.minX,
                  top: obj.y - bounds.minY,
                  width: obj.width || 150,
                  height: obj.height || 55,
                  backgroundColor: allSelected ? '#ddd6fe' : '#d4d4d8',
                  border: allSelected ? '3px solid #8b5cf6' : '3px solid #a1a1aa',
                  borderRadius: '14px 14px 8px 8px',
                  zIndex: 40
                }}
              >
                <div 
                  className="absolute rounded-t-lg"
                  style={{
                    left: 4, top: 4,
                    width: (obj.width || 150) - 8,
                    height: 14,
                    backgroundColor: allSelected ? '#c4b5fd' : '#a1a1aa',
                    borderRadius: '8px 8px 0 0',
                  }}
                />
              </div>
              {/* Sofa seats */}
              {obj.seats?.map((seat, idx) => {
                const seatId = `${zonePrefix}${sofaLabel}-${seat.seat_number || idx + 1}`;
                const isSelected = selectedSeats.includes(seatId);
                const seatWidth = ((obj.width || 150) - 16) / count;
                // Use custom seat color if set
                const customSeatColor = obj.properties?.seat_color;
                const availableColor = customSeatColor || SEAT_COLORS.available;
                const bgColor = isSelected ? SEAT_COLORS.selected : availableColor;
                const textColor = getContrastingTextColor(bgColor);
                
                return (
                  <button
                    key={idx}
                    onClick={() => toggleSeat(seatId, 'sofa', sofaLabel)}
                    className={`absolute flex items-center justify-center font-bold shadow-md transition-all ${
                      isSelected ? 'ring-2 ring-purple-700 scale-110' : 'hover:scale-105'
                    }`}
                    style={{
                      left: obj.x - bounds.minX + 8 + idx * seatWidth,
                      top: obj.y - bounds.minY + 22,
                      width: seatWidth - 4,
                      height: (obj.height || 55) - 30,
                      borderRadius: '6px',
                      backgroundColor: bgColor,
                      color: textColor,
                      fontSize: '10px',
                      zIndex: 41
                    }}
                    title={seatId}
                  >
                    {seat.seat_number || idx + 1}
                  </button>
                );
              })}
            </div>
          );
        })}
        
        {/* Facilities */}
        {layout.canvas_objects?.filter(obj => 
          ['bar', 'toilet', 'entrance', 'exit'].includes(obj.type)
        ).map(obj => (
          <div 
            key={obj.id}
            className="absolute flex items-center justify-center rounded-lg shadow"
            style={{
              left: obj.x - bounds.minX,
              top: obj.y - bounds.minY,
              width: obj.width || 80,
              height: obj.height || 60,
              backgroundColor: obj.color || '#6b7280',
              zIndex: 3
            }}
          >
            <span className="text-xs font-medium text-white">{obj.label}</span>
          </div>
        ))}
      </div>
    );
  };

  // BUS LAYOUT MODE RENDER - Flight Style
  const renderBusLayoutMode = () => {
    const busLayout = layout?.canvas_objects?.find(obj => obj.type === 'bus_layout');
    if (!busLayout) return null;
    
    const seats = processSeatsAuto();

    // Derive row_count / left_seats / right_seats from ACTUAL seats data when
    // properties are missing (legacy layouts created before the dashboard-aligned
    // bus schema was introduced). This prevents the widget from rendering
    // default 9 rows × 4 seats = 36 when the layout actually has fewer seats.
    const busSeats = busLayout.seats || [];
    const derivedRowCount = busSeats.length > 0
      ? Math.max(1, Math.max(...busSeats.map(s => (s.row ?? 0))) + 1)
      : 0;
    const derivedLeftSeats = busSeats.filter(s => s.side === 'left' && (s.row === 0 || s.row === undefined || s.row === null)).length || null;
    const derivedRightSeats = busSeats.filter(s => s.side === 'right' && (s.row === 0 || s.row === undefined || s.row === null)).length || null;

    const rowCount = busLayout.properties?.row_count || derivedRowCount || 1;
    // Support per-side row counts for different left/right configurations (e.g., door on one side)
    const leftRowCount = busLayout.properties?.left_row_count ?? rowCount;
    const rightRowCount = busLayout.properties?.right_row_count ?? rowCount;
    const maxRowCount = Math.max(leftRowCount, rightRowCount);
    
    const leftSeats = busLayout.properties?.left_seats || derivedLeftSeats || 2;
    const rightSeats = busLayout.properties?.right_seats || derivedRightSeats || 2;
    const startRowNum = busLayout.properties?.start_row_number || 1;
    const columnLetters = busLayout.properties?.column_letters || 'ABCDEFGH';
    
    // New settings from canvas
    const frontAtTop = busLayout.properties?.front_at_top !== false; // default true
    const showDriverSeat = busLayout.properties?.has_driver_seat !== false; // default true
    
    // Generate row indices - if front NOT at top, reverse the order
    const rowIndices = frontAtTop 
      ? Array.from({ length: maxRowCount }, (_, i) => i)
      : Array.from({ length: maxRowCount }, (_, i) => maxRowCount - 1 - i);
    
    return (
      <div 
        className="py-4 px-2 sm:py-6 sm:px-4 flex justify-center overflow-x-auto"
        style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
      >
        {/* Bus Container - Responsive */}
        <div 
          className="relative p-3 sm:p-4"
          style={{ 
            backgroundColor: '#f8fafc',
            border: '4px solid #94a3b8',
            borderRadius: frontAtTop ? '40px 40px 20px 20px' : '20px 20px 40px 40px',
            minWidth: '240px',
            maxWidth: '100%',
            width: 'fit-content'
          }}
        >
          {/* Driver Area - only shown if frontAtTop AND showDriverSeat */}
          {frontAtTop && showDriverSeat && (
            <div className="flex items-center justify-between mb-4 pb-4 border-b-2 border-dashed border-slate-300">
              <div className="flex flex-col items-center bg-blue-100 px-2 py-1 rounded-lg">
                <ArrowUp size={14} className="text-blue-600" />
                <span className="text-[8px] text-blue-600 font-bold">FRONT</span>
              </div>
              <div className="w-10 h-10 bg-slate-600 rounded-lg flex items-center justify-center shadow-md">
                <Bus size={20} className="text-white" />
              </div>
              <div className="w-8 h-8 border-3 border-slate-400 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-slate-400 rounded-full" />
              </div>
            </div>
          )}
          
          {/* Front indicator without driver seat */}
          {frontAtTop && !showDriverSeat && (
            <div className="flex items-center justify-center mb-4 pb-4 border-b-2 border-dashed border-slate-300">
              <div className="flex flex-col items-center bg-blue-100 px-2 py-1 rounded-lg">
                <ArrowUp size={14} className="text-blue-600" />
                <span className="text-[8px] text-blue-600 font-bold">FRONT</span>
              </div>
            </div>
          )}
          
          {/* Back indicator at top when front is NOT at top */}
          {!frontAtTop && (
            <div className="flex items-center justify-center mb-4 pb-4 border-b-2 border-dashed border-slate-300">
              <span className="text-xs text-slate-400 font-medium tracking-wider">BACK</span>
            </div>
          )}
          
          {/* Seat Legend - explain icons and colors - only show when applicable */}
          {(() => {
            const hasGroupRules = busLayout.properties?.booking_rules?.some(r => r.policy === 'group_only');
            const preventMiddle = busLayout.properties?.prevent_middle_seat;
            const hasThreePlusSeats = leftSeats >= 3 || rightSeats >= 3;
            const showMiddleLegend = preventMiddle && hasThreePlusSeats;
            
            if (!hasGroupRules && !showMiddleLegend) return null;
            
            return (
              <div className="mb-3 p-2 bg-slate-100 rounded-lg">
                <div className="flex flex-wrap items-center justify-center gap-3 text-[9px]">
                  {hasGroupRules && (
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 bg-green-500 rounded flex items-center justify-center relative">
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-[5px] text-white font-bold">G</span>
                        </span>
                      </div>
                      <span className="text-slate-600">Group booking (must book together)</span>
                    </div>
                  )}
                  {showMiddleLegend && (
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 bg-green-500 rounded flex items-center justify-center relative">
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-500 rounded-full flex items-center justify-center">
                          <span className="text-[5px] text-white font-bold">M</span>
                        </span>
                      </div>
                      <span className="text-slate-600">Select edge seats first</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          
          {/* Seats Grid */}
          <div className="space-y-2">
            {rowIndices.map((rowIdx) => {
              const rowNumber = startRowNum + rowIdx;
              
              // New gap_rules system (replaces old space configuration)
              const gapRules = busLayout.properties?.gap_rules || [];
              
              // Check if there's a gap rule that applies after the previous row
              const gapAfterPrevRow = gapRules.find(g => g.afterRowNumber === rowNumber - 1);
              
              // Per-row custom configuration
              const customRowsEnabled = busLayout.properties?.custom_rows_enabled;
              const customRows = busLayout.properties?.custom_rows || {};
              const rowConfig = customRows[rowNumber] || {};
              const actualLeftSeats = customRowsEnabled && rowConfig.left_seats !== undefined ? rowConfig.left_seats : leftSeats;
              const actualRightSeats = customRowsEnabled && rowConfig.right_seats !== undefined ? rowConfig.right_seats : rightSeats;
              
              // Check if this row is within per-side row counts
              const showLeftSide = rowIdx < leftRowCount;
              const showRightSide = rowIdx < rightRowCount;
              
              // Booking rules - check if this row has any rules
              const bookingRules = busLayout.properties?.booking_rules || [];
              
              // Helper function to check if a seat has a group booking rule
              const getSeatBookingRule = (seatRef, side) => {
                const rowMatch = seatRef?.match(/^(\d+)([A-Z]+)$/);
                const rowNum = rowMatch ? parseInt(rowMatch[1]) : null;
                
                return bookingRules.find(rule => {
                  const rowMatches = rule.rows === 'all' || 
                    (Array.isArray(rule.rows) && rule.rows.includes(rowNum));
                  const sideMatches = rule.side === 'both' || rule.side === side;
                  return rowMatches && sideMatches && rule.policy === 'group_only';
                });
              };
              
              // Check if prevent middle seat is enabled
              const preventMiddleSeat = busLayout.properties?.prevent_middle_seat;
              
              // Helper to check if a seat is a middle seat
              const isMiddleSeatPosition = (seatIdx, totalSeats) => {
                return totalSeats >= 3 && seatIdx > 0 && seatIdx < totalSeats - 1;
              };
              
              return (
                <React.Fragment key={rowIdx}>
                  {/* Gap divider before this row (if gap rule exists for previous row) */}
                  {gapAfterPrevRow && (
                    <div className="flex items-start justify-center">
                      {/* Left side gap area */}
                      <div 
                        className="flex-1 flex items-center justify-center"
                        style={{
                          height: (gapAfterPrevRow.applyTo === 'left' || gapAfterPrevRow.applyTo === 'both' || !gapAfterPrevRow.applyTo) 
                            ? `${gapAfterPrevRow.gapHeightPx || 30}px` 
                            : '4px',
                          marginTop: '4px',
                          marginBottom: '4px',
                          backgroundColor: (gapAfterPrevRow.applyTo === 'left' || gapAfterPrevRow.applyTo === 'both' || !gapAfterPrevRow.applyTo) 
                            ? (gapAfterPrevRow.showDivider ? 'transparent' : 'rgba(148, 163, 184, 0.2)')
                            : 'transparent',
                          borderTop: (gapAfterPrevRow.applyTo === 'left' || gapAfterPrevRow.applyTo === 'both' || !gapAfterPrevRow.applyTo) && gapAfterPrevRow.showDivider 
                            ? `2px ${gapAfterPrevRow.dividerStyle || 'dashed'} #94a3b8` 
                            : 'none',
                          borderBottom: (gapAfterPrevRow.applyTo === 'left' || gapAfterPrevRow.applyTo === 'both' || !gapAfterPrevRow.applyTo) && gapAfterPrevRow.showDivider 
                            ? `2px ${gapAfterPrevRow.dividerStyle || 'dashed'} #94a3b8` 
                            : 'none',
                        }}
                      >
                        {(gapAfterPrevRow.applyTo === 'left' || gapAfterPrevRow.applyTo === 'both' || !gapAfterPrevRow.applyTo) && 
                         gapAfterPrevRow.showDivider && gapAfterPrevRow.dividerLabel && (
                          <span className="text-[8px] text-slate-500 bg-white px-1 font-medium whitespace-nowrap">
                            {gapAfterPrevRow.dividerLabel}
                          </span>
                        )}
                      </div>
                      
                      {/* Center aisle spacer */}
                      <div style={{ width: '28px', height: '4px' }} />
                      
                      {/* Right side gap area */}
                      <div 
                        className="flex-1 flex items-center justify-center"
                        style={{
                          height: (gapAfterPrevRow.applyTo === 'right' || gapAfterPrevRow.applyTo === 'both') 
                            ? `${gapAfterPrevRow.gapHeightPx || 30}px` 
                            : '4px',
                          marginTop: '4px',
                          marginBottom: '4px',
                          backgroundColor: (gapAfterPrevRow.applyTo === 'right' || gapAfterPrevRow.applyTo === 'both') 
                            ? (gapAfterPrevRow.showDivider ? 'transparent' : 'rgba(148, 163, 184, 0.2)')
                            : 'transparent',
                          borderTop: (gapAfterPrevRow.applyTo === 'right' || gapAfterPrevRow.applyTo === 'both') && gapAfterPrevRow.showDivider 
                            ? `2px ${gapAfterPrevRow.dividerStyle || 'dashed'} #94a3b8` 
                            : 'none',
                          borderBottom: (gapAfterPrevRow.applyTo === 'right' || gapAfterPrevRow.applyTo === 'both') && gapAfterPrevRow.showDivider 
                            ? `2px ${gapAfterPrevRow.dividerStyle || 'dashed'} #94a3b8` 
                            : 'none',
                        }}
                      >
                        {(gapAfterPrevRow.applyTo === 'right' || gapAfterPrevRow.applyTo === 'both') && 
                         gapAfterPrevRow.showDivider && gapAfterPrevRow.dividerLabel && 
                         gapAfterPrevRow.applyTo !== 'both' && (
                          <span className="text-[8px] text-slate-500 bg-white px-1 font-medium whitespace-nowrap">
                            {gapAfterPrevRow.dividerLabel}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-center gap-1">
                    {/* Left side seats - only show if row is within left_row_count */}
                    {showLeftSide ? (
                      Array.from({ length: actualLeftSeats }, (_, seatIdx) => {
                      const seatLetter = columnLetters[seatIdx] || String.fromCharCode(65 + seatIdx);
                      const seatRef = `${rowNumber}${seatLetter}`;
                      const seat = seats.find(s => s.seat_ref === seatRef || s.label === seatRef);
                      
                      // Check if this is a middle seat
                      const isMiddleSeat = preventMiddleSeat && isMiddleSeatPosition(seatIdx, actualLeftSeats);
                      
                      // Fix: Use strict comparison with seatRef as primary identifier
                      const isSelected = selectedSeats.some(s => {
                        if (typeof s === 'object') {
                          // Object format: compare seat_ref strictly
                          return s.seat_ref === seatRef;
                        }
                        // String format: compare directly
                        return s === seatRef;
                      });
                      
                      const isBooked = seat?.status === 'booked';
                      const isHeld = seat?.status === 'held';
                      
                      // Check if this seat has a group booking rule
                      const groupRule = getSeatBookingRule(seatRef, 'left');
                      const hasGroupRule = !!groupRule;
                      
                      let bgColor = SEAT_COLORS.available;
                      if (isBooked) bgColor = SEAT_COLORS.booked;
                      else if (isHeld) bgColor = SEAT_COLORS.held || '#f59e0b';
                      else if (isSelected) bgColor = SEAT_COLORS.selected;
                      
                      // Seat is clickable if not booked
                      const isClickable = !isBooked;
                      
                      return (
                        <button
                          key={seatRef}
                          onClick={() => toggleSeat(seat?.id, 'bus_layout', seatRef)}
                          disabled={!isClickable}
                          className={`
                            w-8 h-8 sm:w-10 sm:h-10 rounded-t-lg rounded-b-sm text-[8px] sm:text-[10px] font-bold transition-all
                            flex items-center justify-center relative
                            ${isClickable ? 'hover:scale-105 cursor-pointer' : 'cursor-not-allowed'}
                            ${isSelected ? 'ring-2 ring-offset-1 ring-blue-500' : ''}
                            ${hasGroupRule && !isSelected ? 'ring-2 ring-purple-400 ring-dashed' : ''}
                            ${isMiddleSeat && !isSelected && !hasGroupRule ? 'ring-2 ring-amber-400' : ''}
                          `}
                          style={{ 
                            backgroundColor: bgColor,
                            color: '#fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                          }}
                          title={`${seatRef} - ${seat?.status || 'available'}${hasGroupRule ? ' (Group booking required)' : ''}${isMiddleSeat ? ' (Select edge seats first)' : ''}`}
                        >
                          {seatRef}
                          {hasGroupRule && !isSelected && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-purple-500 rounded-full flex items-center justify-center">
                              <span className="text-[5px] sm:text-[6px] text-white">G</span>
                            </span>
                          )}
                          {isMiddleSeat && !isSelected && !hasGroupRule && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-amber-500 rounded-full flex items-center justify-center">
                              <span className="text-[5px] sm:text-[6px] text-white">M</span>
                            </span>
                          )}
                        </button>
                      );
                    })
                    ) : (
                      /* Empty placeholder when left side has no seats for this row */
                      Array.from({ length: leftSeats }, (_, idx) => (
                        <div key={`empty-left-${idx}`} className="w-8 h-8 sm:w-10 sm:h-10" />
                      ))
                    )}
                    
                    {/* Show empty placeholders if left side has fewer seats */}
                    {showLeftSide && actualLeftSeats < leftSeats && Array.from({ length: leftSeats - actualLeftSeats }, (_, idx) => (
                      <div key={`empty-left-${idx}`} className="w-10 h-10" />
                    ))}
                    
                    {/* Aisle */}
                  <div className="w-5 h-10 flex items-center justify-center">
                    <div className="w-0.5 h-full bg-slate-200 rounded-full" />
                  </div>
                  
                  {/* Right side seats - only show if row is within right_row_count */}
                  {showRightSide ? (
                    Array.from({ length: actualRightSeats }, (_, seatIdx) => {
                    const seatLetter = columnLetters[leftSeats + seatIdx] || String.fromCharCode(65 + leftSeats + seatIdx);
                    const seatRef = `${rowNumber}${seatLetter}`;
                    const seat = seats.find(s => s.seat_ref === seatRef || s.label === seatRef);
                    
                    // Check if this is a middle seat (right side)
                    const isMiddleSeat = preventMiddleSeat && isMiddleSeatPosition(seatIdx, actualRightSeats);
                    
                    // Fix: Use strict comparison with seatRef as primary identifier
                    const isSelected = selectedSeats.some(s => {
                      if (typeof s === 'object') {
                        // Object format: compare seat_ref strictly
                        return s.seat_ref === seatRef;
                      }
                      // String format: compare directly
                      return s === seatRef;
                    });
                    
                    const isBooked = seat?.status === 'booked';
                    const isHeld = seat?.status === 'held';
                    
                    // Check if this seat has a group booking rule
                    const groupRule = getSeatBookingRule(seatRef, 'right');
                    const hasGroupRule = !!groupRule;
                    
                    let bgColor = SEAT_COLORS.available;
                    if (isBooked) bgColor = SEAT_COLORS.booked;
                    else if (isHeld) bgColor = SEAT_COLORS.held || '#f59e0b';
                    else if (isSelected) bgColor = SEAT_COLORS.selected;
                    
                    // Seat is clickable if not booked
                    const isClickable = !isBooked;
                    
                    return (
                      <button
                        key={seatRef}
                        onClick={() => toggleSeat(seat?.id, 'bus_layout', seatRef)}
                        disabled={!isClickable}
                        className={`
                          w-8 h-8 sm:w-10 sm:h-10 rounded-t-lg rounded-b-sm text-[8px] sm:text-[10px] font-bold transition-all
                          flex items-center justify-center relative
                          ${isClickable ? 'hover:scale-105 cursor-pointer' : 'cursor-not-allowed'}
                          ${isSelected ? 'ring-2 ring-offset-1 ring-blue-500' : ''}
                          ${hasGroupRule && !isSelected ? 'ring-2 ring-purple-400 ring-dashed' : ''}
                          ${isMiddleSeat && !isSelected && !hasGroupRule ? 'ring-2 ring-amber-400' : ''}
                        `}
                        style={{ 
                          backgroundColor: bgColor,
                          color: '#fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                        }}
                        title={`${seatRef} - ${seat?.status || 'available'}${hasGroupRule ? ' (Group booking required)' : ''}${isMiddleSeat ? ' (Select edge seats first)' : ''}`}
                      >
                        {seatRef}
                        {hasGroupRule && !isSelected && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-[5px] sm:text-[6px] text-white">G</span>
                          </span>
                        )}
                        {isMiddleSeat && !isSelected && !hasGroupRule && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-amber-500 rounded-full flex items-center justify-center">
                            <span className="text-[5px] sm:text-[6px] text-white">M</span>
                          </span>
                        )}
                      </button>
                    );
                  })
                  ) : (
                    /* Empty placeholder when right side has no seats for this row */
                    Array.from({ length: rightSeats }, (_, idx) => (
                      <div key={`empty-right-${idx}`} className="w-8 h-8 sm:w-10 sm:h-10" />
                    ))
                  )}
                  
                  {/* Show empty placeholders if right side has fewer seats */}
                  {showRightSide && actualRightSeats < rightSeats && Array.from({ length: rightSeats - actualRightSeats }, (_, idx) => (
                    <div key={`empty-right-${idx}`} className="w-10 h-10" />
                  ))}
                </div>
                </React.Fragment>
              );
            })}
          </div>
          
          {/* Back indicator - or Front indicator if orientation is flipped */}
          {frontAtTop ? (
            <div className="mt-4 pt-2 border-t-2 border-dashed border-slate-300 text-center">
              <span className="text-xs text-slate-400 font-medium tracking-wider">BACK</span>
            </div>
          ) : (
            <>
              {/* When front is at bottom, show back at top and front at bottom */}
              <div className="mt-4 pt-4 border-t-2 border-dashed border-slate-300 flex items-center justify-between">
                <div className="flex flex-col items-center bg-blue-100 px-2 py-1 rounded-lg">
                  <ArrowDown size={14} className="text-blue-600" />
                  <span className="text-[8px] text-blue-600 font-bold">FRONT</span>
                </div>
                {showDriverSeat && (
                  <>
                    <div className="w-10 h-10 bg-slate-600 rounded-lg flex items-center justify-center shadow-md">
                      <Bus size={20} className="text-white" />
                    </div>
                    <div className="w-8 h-8 border-3 border-slate-400 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-slate-400 rounded-full" />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          
          {/* Bus features (toilet, door, TV) - positioned based on canvas coordinates */}
          {layout?.canvas_objects?.filter(obj => obj.type.startsWith('bus_') && obj.type !== 'bus_layout').map(obj => {
            let Icon, label, bgColor;
            if (obj.type === 'bus_toilet') {
              Icon = Bath;
              label = 'WC';
              bgColor = '#06b6d4';
            } else if (obj.type.includes('door')) {
              Icon = DoorOpen;
              label = obj.properties?.door_position?.toUpperCase() || 'DOOR';
              bgColor = '#f59e0b';
            } else if (obj.type === 'bus_tv') {
              Icon = Tv;
              label = 'TV';
              bgColor = '#8b5cf6';
            } else if (obj.type === 'bus_aisle') {
              // Render aisle/divider
              return (
                <div 
                  key={obj.id}
                  className="w-full flex items-center justify-center py-2 my-2 border-t-2 border-b-2 border-dashed"
                  style={{ borderColor: '#94a3b8' }}
                >
                  <span className="text-[9px] text-slate-400 bg-white px-2 font-medium">
                    {obj.label || 'SECTION DIVIDER'}
                  </span>
                </div>
              );
            } else {
              return null;
            }
            
            // Calculate position relative to bus container
            // Default positions based on object type if no coordinates
            const busLayout = layout?.canvas_objects?.find(o => o.type === 'bus_layout');
            const busWidth = busLayout?.width || 250;
            const busHeight = busLayout?.height || 500;
            
            // Use normalized position or default based on type
            let posStyle = {};
            if (obj.x !== undefined && obj.y !== undefined && busLayout) {
              // Normalize position relative to bus container
              const normalizedX = (obj.x - (busLayout.x || 0)) / busWidth;
              const normalizedY = (obj.y - (busLayout.y || 0)) / busHeight;
              
              if (normalizedX < 0.3) {
                posStyle.left = '5px';
              } else if (normalizedX > 0.7) {
                posStyle.right = '5px';
              } else {
                posStyle.left = '50%';
                posStyle.transform = 'translateX(-50%)';
              }
              
              if (normalizedY < 0.2) {
                posStyle.top = '80px';
              } else if (normalizedY > 0.8) {
                posStyle.bottom = '60px';
              } else {
                posStyle.top = `${normalizedY * 100}%`;
              }
            } else {
              // Default positions
              if (obj.type === 'bus_toilet') {
                posStyle = { right: '5px', bottom: '60px' };
              } else if (obj.type.includes('door')) {
                posStyle = { left: '5px', top: obj.type.includes('front') ? '80px' : obj.type.includes('back') ? 'auto' : '50%' };
                if (obj.type.includes('back')) posStyle.bottom = '60px';
              } else if (obj.type === 'bus_tv') {
                posStyle = { left: '50%', top: '80px', transform: 'translateX(-50%)' };
              }
            }
            
            return (
              <div 
                key={obj.id}
                className="absolute flex flex-col items-center justify-center rounded-lg z-10"
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: bgColor,
                  ...posStyle
                }}
              >
                <Icon size={16} className="text-white" />
                <span className="text-[7px] text-white font-bold">{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // AUTO MODE RENDER
  const renderAutoMode = () => {
    const seats = processSeatsAuto();
    
    // Group by row
    const seatsByRow = seats.reduce((acc, seat) => {
      if (!acc[seat.row]) acc[seat.row] = [];
      acc[seat.row].push(seat);
      return acc;
    }, {});

    // Separate zone rows from non-zone rows
    const zoneRows = {};
    const defaultRows = {};
    
    Object.keys(seatsByRow).forEach(rowLabel => {
      if (rowLabel.includes('📍')) {
        // Extract zone name from "📍 Zone 1 - Row A" format
        const match = rowLabel.match(/📍\s*(.+?)\s*-\s*Row\s*(.+)/);
        if (match) {
          const zoneName = match[1].trim();
          const actualRowLabel = match[2].trim();
          if (!zoneRows[zoneName]) zoneRows[zoneName] = {};
          zoneRows[zoneName][actualRowLabel] = {
            seats: seatsByRow[rowLabel],
            originalLabel: rowLabel
          };
        } else {
          defaultRows[rowLabel] = seatsByRow[rowLabel];
        }
      } else if (rowLabel.includes('🛋️') || rowLabel.includes('🪑')) {
        defaultRows[rowLabel] = seatsByRow[rowLabel];
      } else {
        defaultRows[rowLabel] = seatsByRow[rowLabel];
      }
    });

    // Sort default rows
    const sortedDefaultRows = Object.keys(defaultRows).sort((a, b) => {
      const aIsSofa = a.includes('🛋️');
      const bIsSofa = b.includes('🛋️');
      if (aIsSofa && !bIsSofa) return 1;
      if (!aIsSofa && bIsSofa) return -1;
      const aIsTable = a.includes('🪑');
      const bIsTable = b.includes('🪑');
      if (aIsTable && !bIsTable) return 1;
      if (!aIsTable && bIsTable) return -1;
      return a.localeCompare(b);
    });

    // Sort zone names
    const sortedZoneNames = Object.keys(zoneRows).sort();

    // Find the maximum row width to determine if scrolling is needed
    const allRows = [...Object.values(defaultRows), ...Object.values(zoneRows).flatMap(z => Object.values(z).map(r => r.seats))];
    const maxRowWidth = Math.max(...allRows.map(rowSeats => {
      if (!rowSeats || !rowSeats.length) return 0;
      const seats = Array.isArray(rowSeats) ? rowSeats : rowSeats;
      const firstSeat = seats[0];
      const getSeatStyle = (type) => {
        switch (type) {
          case 'sofa': return { width: 36 };
          case 'round_table': 
          case 'rect_table': return { width: 32 };
          case 'zone_seat': return { width: 30 };
          default: return { width: 30 };
        }
      };
      const style = getSeatStyle(firstSeat?.type);
      return seats.length * (style.width + 8) + 150;
    }));

    // Helper function to render a single row
    const renderRow = (rowLabel, rowSeats, displayLabel) => {
      const sortedSeats = [...rowSeats].sort((a, b) => a.seatNum - b.seatNum);
      const firstSeat = sortedSeats[0];
      const curve = firstSeat?.curve || 0;
      const hasCurve = Math.abs(curve) > 15;
      
      const getSeatStyle = (type) => {
        switch (type) {
          case 'sofa': return { width: 36, height: 28, radius: '8px' };
          case 'round_table': 
          case 'rect_table': return { width: 32, height: 32, radius: '50%' };
          case 'zone_seat': return { width: 30, height: 26, radius: '6px' };
          default: return { width: 30, height: 26, radius: '6px 6px 3px 3px' };
        }
      };
      
      const style = getSeatStyle(firstSeat?.type);
      
      return (
        <div key={rowLabel} className="inline-flex items-center mb-2 sm:mb-3 relative">
          {/* Left row label */}
          <span className="text-gray-600 text-[10px] sm:text-xs md:text-sm font-bold min-w-[20px] sm:min-w-[24px] md:min-w-[30px] mr-1 sm:mr-2 text-right flex-shrink-0">
            {displayLabel}
          </span>
          <div className="inline-flex gap-0.5 sm:gap-1 relative" style={hasCurve ? { height: `${style.height + 30}px` } : {}}>
            {sortedSeats.map((seat, idx) => {
              const isSelected = selectedSeats.includes(seat.id);
              
              let posStyle = {};
              if (hasCurve) {
                const centerIdx = (sortedSeats.length - 1) / 2;
                const normalizedPos = (idx - centerIdx) / Math.max(centerIdx, 1);
                const curveDepth = Math.min(Math.abs(curve) / 10, 25);
                const yOffset = curveDepth * normalizedPos * normalizedPos;
                posStyle = {
                  position: 'absolute',
                  left: `${idx * (style.width + 4)}px`,
                  top: `${curve > 0 ? yOffset : -yOffset}px`
                };
              }
              
              // Mobile-optimized sizes - more compact
              const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
              const mobileWidth = Math.max(style.width - 8, 22);
              const mobileHeight = Math.max(style.height - 6, 20);
              
              return (
                <button
                  key={seat.id}
                  onClick={() => toggleSeat(seat.id, seat.objType, seat.objLabel)}
                  title={seat.id}
                  className={`flex items-center justify-center font-bold shadow-sm transition-all flex-shrink-0 touch-manipulation ${
                    isSelected ? 'ring-2 ring-blue-700 scale-105 sm:scale-110 z-10' : 'hover:scale-105 active:scale-95'
                  }`}
                  style={{
                    width: `${isMobile ? mobileWidth : style.width}px`,
                    height: `${isMobile ? mobileHeight : style.height}px`,
                    minWidth: '20px',
                    minHeight: '18px',
                    borderRadius: style.radius,
                    backgroundColor: isSelected ? SEAT_COLORS.selected : SEAT_COLORS.available,
                    fontSize: isMobile ? '7px' : '10px',
                    color: '#fff',
                    ...posStyle
                  }}
                >
                  {seat.displayLabel}
                </button>
              );
            })}
          </div>
          {/* Right row label */}
          <span className="text-gray-600 text-[10px] sm:text-xs md:text-sm font-bold min-w-[20px] sm:min-w-[24px] md:min-w-[30px] ml-1 sm:ml-2 text-left flex-shrink-0">
            {displayLabel}
          </span>
        </div>
      );
    };

    return (
      <div 
        className="py-4 sm:py-6 px-2 sm:px-4"
        style={{ 
          transform: `scale(${zoom})`, 
          transformOrigin: 'top center',
          minWidth: maxRowWidth > 1600 ? `${maxRowWidth}px` : 'auto',
          display: 'inline-block',
          minHeight: '100%'
        }}
      >
        {/* Screen objects - fixed width, centered */}
        {layout.canvas_objects?.filter(obj => obj.type === 'screen').map(obj => (
          <div 
            key={obj.id}
            className="mb-4 rounded-lg shadow-lg mx-auto flex items-center justify-center"
            style={{ 
              backgroundColor: obj.color || '#6b7280',
              width: `${Math.min(obj.width || 400, 600)}px`,
              padding: '12px 32px'
            }}
          >
            <span className="text-white font-bold text-base tracking-widest">{obj.label || 'SCREEN'}</span>
          </div>
        ))}
        
        {/* Stage & DJ Booth - fixed width, centered */}
        {layout.canvas_objects?.filter(obj => obj.type === 'stage' || obj.type === 'dj_booth').map(obj => (
          <div 
            key={obj.id}
            className="mb-4 rounded-b-2xl shadow-lg mx-auto flex items-center justify-center"
            style={{ 
              backgroundColor: obj.type === 'stage' ? '#a855f7' : '#06b6d4',
              width: `${Math.min(obj.width || 200, 400)}px`,
              padding: '8px 24px'
            }}
          >
            <span className="text-white font-medium text-sm tracking-wider">{obj.label || 'STAGE'}</span>
          </div>
        ))}

        {/* Rows container */}
        <div className="flex flex-col items-start">
          {/* Default zone rows (no box) */}
          {sortedDefaultRows.map(rowLabel => renderRow(rowLabel, defaultRows[rowLabel], rowLabel))}
          
          {/* Zone rows (boxed with zone label) */}
          {sortedZoneNames.map(zoneName => {
            const zoneRowData = zoneRows[zoneName];
            const sortedZoneRowLabels = Object.keys(zoneRowData).sort();
            
            return (
              <div 
                key={zoneName}
                className="mt-4 mb-2 rounded-lg p-3 relative"
                style={{
                  border: '2px dashed #9ca3af',
                  backgroundColor: 'rgba(249, 250, 251, 0.5)'
                }}
              >
                {/* Zone label - positioned at top */}
                <div className="absolute -top-3 left-4 bg-white px-2">
                  <span className="text-gray-600 font-bold text-sm">📍 {zoneName}</span>
                </div>
                
                {/* Rows inside the zone */}
                <div className="flex flex-col items-start pt-2">
                  {sortedZoneRowLabels.map(rowLabel => {
                    const rowData = zoneRowData[rowLabel];
                    return renderRow(rowData.originalLabel, rowData.seats, rowLabel);
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Extract event info from layout
  const eventInfo = layout?.event_name ? {
    name: layout.event_name,
    date: layout.event_date,
    time: layout.event_time,
    location: layout.event_location || layout.venue_name
  } : null;

  return (
    <div 
      className={`
        ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''}
        ${embedMode ? 'w-full h-full' : 'min-h-screen bg-gray-50'}
      `}
    >
      {/* Header */}
      {!hideHeader && !embedMode && (
        <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-2 sm:py-3 sticky top-0 z-40 shadow-sm">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex-1 min-w-0">
              {eventInfo ? (
                <>
                  <h1 className="text-gray-900 font-semibold text-sm sm:text-lg truncate">{eventInfo.name}</h1>
                  <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-gray-500 text-xs">
                    {eventInfo.date && <span className="whitespace-nowrap">📅 {eventInfo.date}</span>}
                    {eventInfo.time && <span className="whitespace-nowrap">🕐 {eventInfo.time}</span>}
                    {eventInfo.location && <span className="whitespace-nowrap truncate max-w-[120px] sm:max-w-none">📍 {eventInfo.location}</span>}
                    {!eventInfo.date && !eventInfo.time && !eventInfo.location && (
                      <span>Select your seats</span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <h1 className="text-gray-900 font-semibold text-sm sm:text-lg truncate">{layout.name}</h1>
                  <p className="text-gray-500 text-xs">Select your seats</p>
                </>
              )}
            </div>
            <button onClick={() => setIsFullscreen(!isFullscreen)} className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 text-gray-600 flex-shrink-0 ml-2">
              {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          </div>
        </div>
      )}

      {/* Preview Mode Banner */}
      {isPreviewMode && (
        <div className="bg-amber-500 text-white px-3 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-medium flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2">
            <span>👁️</span>
            <span><strong>PREVIEW MODE</strong> - This layout is not published</span>
          </div>
          <button
            onClick={async () => {
              try {
                // Use PATCH for partial update (only is_published field)
                await axios.patch(`${API}/admin/layouts/${layoutId}`, { is_published: true }, { withCredentials: true });
                toast.success('Layout published successfully!');
                setLayout(prev => ({ ...prev, is_published: true }));
              } catch (err) {
                // Handle different error formats
                const errorDetail = err.response?.data?.detail;
                let errorMessage = 'Failed to publish layout';
                if (typeof errorDetail === 'string') {
                  errorMessage = errorDetail;
                } else if (Array.isArray(errorDetail)) {
                  errorMessage = errorDetail.map(e => e.msg || String(e)).join(', ');
                } else if (errorDetail && typeof errorDetail === 'object') {
                  errorMessage = errorDetail.msg || errorDetail.message || 'Validation error';
                }
                toast.error(errorMessage);
              }
            }}
            className="bg-white text-amber-600 px-4 py-1.5 rounded-lg font-semibold text-sm hover:bg-amber-50 transition-colors flex items-center gap-1.5 shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Publish Now
          </button>
        </div>
      )}

      {/* Timer Notification - Shows when seats are first selected */}
      {/* Desktop: Top-right corner, Mobile: Bottom floating bar */}
      {showTimerPopup && (
        <>
          {/* Desktop Timer - Top right corner */}
          <div 
            className="hidden sm:block fixed top-4 right-4 z-50 animate-in fade-in slide-in-from-right duration-300"
          >
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-blue-400/30">
              <div className="bg-white/20 rounded-full p-2.5 animate-pulse">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-sm">Seats Reserved!</p>
                <p className="text-blue-100 text-xs">Complete your booking within {holdMinutes} minutes</p>
              </div>
              <button 
                onClick={() => setShowTimerPopup(false)}
                className="ml-2 hover:bg-white/20 rounded-full p-1.5 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Mobile Timer - Top floating notification */}
          <div 
            className="sm:hidden fixed top-4 left-4 right-4 z-50 animate-in fade-in slide-in-from-top duration-300"
          >
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center justify-between border border-blue-400/30">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-full p-2 animate-pulse">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-bold text-sm">Seats Reserved!</p>
                  <p className="text-blue-100 text-[11px]">{holdMinutes} min to complete checkout</p>
                </div>
              </div>
              <button 
                onClick={() => setShowTimerPopup(false)}
                className="hover:bg-white/20 rounded-full p-1.5 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Warning Popup - Shows at 25% time remaining */}
      {/* Desktop: Top-right corner, Mobile: Bottom floating bar */}
      {showWarningPopup && (
        <>
          {/* Desktop Warning - Top right corner */}
          <div 
            className="hidden sm:block fixed top-4 right-4 z-50"
            style={{ animation: 'pulse 1s ease-in-out infinite' }}
          >
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 border-2 border-amber-300/50">
              <div className="bg-white/25 rounded-full p-2.5">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-sm">⏰ Time Almost Up!</p>
                <p className="text-amber-100 text-xs">Only {formatTime(holdTimeLeft)} remaining to complete checkout</p>
              </div>
              <button 
                onClick={() => setShowWarningPopup(false)}
                className="ml-2 hover:bg-white/20 rounded-full p-1.5 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Mobile Warning - Top floating notification */}
          <div 
            className="sm:hidden fixed top-4 left-4 right-4 z-50"
            style={{ animation: 'pulse 1s ease-in-out infinite' }}
          >
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center justify-between border-2 border-amber-300/50">
              <div className="flex items-center gap-3">
                <div className="bg-white/25 rounded-full p-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="font-bold text-sm">⏰ {formatTime(holdTimeLeft)} left!</p>
                  <p className="text-amber-100 text-[11px]">Complete checkout now</p>
                </div>
              </div>
              <button 
                onClick={() => setShowWarningPopup(false)}
                className="hover:bg-white/20 rounded-full p-1.5 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Seating Area */}
      <div className="px-2 sm:px-4 overflow-x-auto overflow-y-auto bg-white" style={{ maxHeight: embedMode ? '100%' : 'calc(100vh - 180px)', paddingBottom: '120px' }}>
        {/* Check if this is a bus layout */}
        {layout?.canvas_objects?.some(obj => obj.type === 'bus_layout') ? (
          renderBusLayoutMode()
        ) : (
          displayMode === 'manual' ? renderManualMode() : renderAutoMode()
        )}
      </div>

      {/* Zoom Controls */}
      <div className="flex flex-col gap-1 z-40" style={{ position: embedMode ? 'absolute' : 'fixed', bottom: embedMode ? '100px' : '110px', right: '12px' }}>
        <button onClick={zoomIn} className="p-2 bg-white hover:bg-gray-100 rounded-lg text-gray-700 shadow-md border border-gray-200"><ZoomIn className="w-4 h-4" /></button>
        <button onClick={resetZoom} className="p-2 bg-white hover:bg-gray-100 rounded-lg text-gray-700 shadow-md border border-gray-200"><RotateCcw className="w-4 h-4" /></button>
        <button onClick={zoomOut} className="p-2 bg-white hover:bg-gray-100 rounded-lg text-gray-700 shadow-md border border-gray-200"><ZoomOut className="w-4 h-4" /></button>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg p-3 z-40 shadow-md border border-gray-200 hidden sm:block" style={{ position: embedMode ? 'absolute' : 'fixed', bottom: embedMode ? '100px' : '110px', left: '12px' }}>
        <div className="flex flex-col gap-2 text-xs">
          <div className="flex items-center gap-2"><div className="w-5 h-4 rounded" style={{ backgroundColor: SEAT_COLORS.available }}></div><span className="text-gray-700">Available</span></div>
          <div className="flex items-center gap-2"><div className="w-5 h-4 rounded" style={{ backgroundColor: SEAT_COLORS.selected }}></div><span className="text-gray-700">Selected</span></div>
          <div className="flex items-center gap-2"><div className="w-5 h-4 rounded" style={{ backgroundColor: SEAT_COLORS.booked }}></div><span className="text-gray-700">Booked</span></div>
        </div>
      </div>

      {/* Checkout Bar */}
      <div className="bg-white border-t border-gray-200 z-50 shadow-lg" style={{ position: embedMode ? 'absolute' : 'fixed', bottom: 0, left: 0, right: 0 }}>
        {/* Category pricing legend */}
        {ticketPricing && ticketPricing.pricing?.length > 0 && (
          <div className="flex justify-center gap-3 py-1.5 border-b border-gray-100 bg-gray-50 flex-wrap px-2">
            {ticketPricing.pricing.map(p => (
              <div key={p.category} className="flex items-center gap-1 text-xs" data-testid={`price-legend-${p.category}`}>
                <span className="text-gray-500">{p.category}:</span>
                <span className="font-semibold text-gray-800">{formatPrice(p.price)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-center gap-4 py-2 border-b border-gray-100 sm:hidden">
          <div className="flex items-center gap-1.5 text-xs"><div className="w-4 h-3 rounded" style={{ backgroundColor: SEAT_COLORS.available }}></div><span className="text-gray-600">Available</span></div>
          <div className="flex items-center gap-1.5 text-xs"><div className="w-4 h-3 rounded" style={{ backgroundColor: SEAT_COLORS.selected }}></div><span className="text-gray-600">Selected</span></div>
          <div className="flex items-center gap-1.5 text-xs"><div className="w-4 h-3 rounded" style={{ backgroundColor: SEAT_COLORS.booked }}></div><span className="text-gray-600">Booked</span></div>
        </div>
        <div className="px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              {selectedSeats.length > 0 ? (
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="text-gray-900 font-bold text-base sm:text-lg">{selectedSeats.length}</span>
                    <span className="text-gray-500 text-xs sm:text-sm">seat{selectedSeats.length > 1 ? 's' : ''}</span>
                    {selectedSeatsPricing && (
                      <div className="flex items-center gap-1 ml-1" data-testid="checkout-total">
                        <span className="text-green-700 font-bold text-sm sm:text-base">{formatPrice(selectedSeatsPricing.grandTotal)}</span>
                        {selectedSeatsPricing.totalFees > 0 && (
                          <span className="text-gray-400 text-[10px] sm:text-xs">(incl. {formatPrice(selectedSeatsPricing.totalFees)} fees)</span>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Mobile: Show first 2 seat labels compactly */}
                  <div className="flex sm:hidden flex-wrap gap-1">
                    {selectedSeats.slice(0, 2).map((s, idx) => {
                      const seatLabel = typeof s === 'object' ? (s.seat_ref || s.label || s.id) : s;
                      return <span key={`${seatLabel}-${idx}`} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium truncate max-w-[70px]">{seatLabel}</span>;
                    })}
                    {selectedSeats.length > 2 && (
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">+{selectedSeats.length - 2}</span>
                    )}
                  </div>
                  {/* Desktop: Show first 5 seat labels */}
                  <div className="hidden sm:flex flex-wrap gap-1">
                    {selectedSeats.slice(0, 5).map((s, idx) => {
                      const seatLabel = typeof s === 'object' ? (s.seat_ref || s.label || s.id) : s;
                      return <span key={`${seatLabel}-${idx}`} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">{seatLabel}</span>;
                    })}
                    {selectedSeats.length > 5 && (<span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">+{selectedSeats.length - 5}</span>)}
                  </div>
                  <div className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-mono font-bold ${holdTimeLeft < 60 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>⏱ {formatTime(holdTimeLeft)}</div>
                  <button onClick={clearAllSeats} className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs text-red-600 hover:bg-red-50 rounded"><X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />Clear</button>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Tap on seats to select</p>
              )}
            </div>
            <Button
              disabled={selectedSeats.length === 0}
              onClick={() => {
                const seatLabels = selectedSeats.map(s => typeof s === 'object' ? (s.seat_ref || s.label || s.id) : s);
                toast.success(`Proceeding with: ${seatLabels.join(', ')}`);
              }}
              className={`px-6 py-2 font-semibold text-sm whitespace-nowrap ${selectedSeats.length > 0 ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
            >
              {selectedSeats.length > 0 ? 'Proceed to Pay' : 'Select Seats'}
            </Button>
          </div>
          {/* Branding - Hide for White Label subscribers */}
          {!isWhiteLabel && (
          <div className="text-center mt-2 pt-2 border-t border-gray-100">
            <span className="text-[10px] text-gray-400">Seating powered by <span className="font-semibold text-gray-500">SeatPilot</span></span>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
